import { format, type UrlObject } from 'url'
import { Button, Link as LinkC, type LinkProps } from '@chakra-ui/react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React, { useCallback } from 'react'

import logger from '@/utils/logger'

import {
  type TrackEventExtraProps,
  type TrackEventProps,
  trackEvent as trackAnalyticsEvent,
} from '@/client/services/analytics'
import { WEBSITE_URL } from '@/client/services/url'
import { getRedirect } from '@/client/services/url/getRedirect'

export type ShwLinkTrackClickProps = Omit<TrackEventProps, 'category'> & {
  extra?: TrackEventExtraProps
}

export type ShwLinkProps = Omit<LinkProps, 'href' | 'target' | 'rel'> & {
  href?: string | UrlObject | undefined
  children: React.ReactNode
  shallow?: boolean
  isUgc?: boolean
  trackEvent?: ShwLinkTrackClickProps
  noScroll?: boolean
}

function getCommonProperties({ children, ...props }: Omit<ShwLinkProps, 'href' | 'trackEvent'>) {
  const isReactElement = React.isValidElement(children)

  const variant = props.variant ?? (isReactElement ? 'unstyled' : 'normal')
  const content = isReactElement
    ? React.cloneElement(children, {
        // biome-ignore lint/suspicious/noTsIgnore: errors in tsc but not tsgo
        // @ts-ignore: TODO: Fix this
        as: children.props.as === 'a' ? undefined : children.props.as,
        cursor: 'pointer',
      })
    : children

  const noTabIndex =
    isReactElement &&
    // @ts-expect-error: `content`'s type is always {} for some reason
    (content?.type === Button || content?.type?.displayName === 'Button') &&
    // @ts-expect-error: `content`'s type is always {} for some reason
    content?.props?.as == null

  return {
    variant,
    tabIndex: noTabIndex ? -1 : 0,
    content,
  }
}

const ShwLinkWithoutHref = React.forwardRef(
  (
    props: ShwLinkProps & { href?: undefined },
    ref: React.ForwardedRef<HTMLAnchorElement>
  ): React.ReactElement => {
    const { variant, tabIndex, content } = getCommonProperties(props)

    return (
      <LinkC
        href={undefined}
        display="inline-block"
        ref={ref}
        variant={variant}
        tabIndex={tabIndex}
        {...props}
      >
        {content}
      </LinkC>
    )
  }
)

const ShwLinkWithHref = React.forwardRef(
  (
    {
      href,
      shallow = false,
      noScroll = false,
      trackEvent,
      isUgc = false,
      ...props
    }: ShwLinkProps & { href: string | UrlObject },
    ref: React.ForwardedRef<HTMLAnchorElement>
  ): React.ReactElement => {
    const { isExternal } = props
    const hrefString = typeof href === 'string' ? href : format(href)
    const trimmedHref =
      // If we want to link directly to just WEBSITE_URL, our trimmedHref should be '/', not ''
      hrefString === WEBSITE_URL
        ? '/'
        : hrefString.startsWith(WEBSITE_URL)
          ? hrefString.substring(WEBSITE_URL.length)
          : hrefString

    const redirectHref = getRedirect(trimmedHref)
    const finalHref = redirectHref ?? trimmedHref

    const isRelative = finalHref.startsWith('/')
    const extraProps: Record<string, unknown> = {}
    const isHrefApiRoute = finalHref.startsWith('/api')

    if (isHrefApiRoute) {
      extraProps.rel = 'nofollow noindex'
    } else if (isUgc === true) {
      extraProps.rel = 'ugc nofollow'
    }

    const { variant, tabIndex, content } = getCommonProperties(props)

    const onClick = useCallback(() => {
      trackAnalyticsEvent(
        { category: 'Link Click', action: trackEvent?.action ?? redirectHref ?? trimmedHref },
        trackEvent?.extra
      )
    }, [redirectHref, trackEvent?.action, trackEvent?.extra, trimmedHref])

    if (isHrefApiRoute) {
      // If href is an API route, we don't want to use Next.js Link pre-fetching
      // because it causes our catch-all [...404] page to be pre-fetched,
      // causing an error.
      return (
        <LinkC
          variant={variant}
          href={finalHref}
          onClick={onClick}
          display="inline-block"
          tabIndex={tabIndex}
          ref={ref}
          {...props}
          {...extraProps}
        >
          {content}
        </LinkC>
      )
    }

    if (isRelative && isExternal !== true) {
      return (
        <LinkC
          variant={variant}
          onClick={onClick}
          display="inline-block"
          tabIndex={tabIndex}
          ref={ref}
          {...props}
          {...extraProps}
          as={Link}
          href={finalHref}
          shallow={shallow}
          scroll={!noScroll}
        >
          {content}
        </LinkC>
      )
    }
    return (
      <LinkC
        variant={variant}
        href={finalHref}
        isExternal={!isRelative || isExternal}
        onClick={onClick}
        display="inline-block"
        tabIndex={tabIndex}
        ref={ref}
        {...props}
        {...extraProps}
      >
        {content}
      </LinkC>
    )
  }
)

export default React.forwardRef(
  (
    { href: hrefOrig, ...props }: ShwLinkProps,
    ref: React.ForwardedRef<HTMLAnchorElement>
  ): React.ReactElement => {
    const pathname = usePathname()

    let href: string | UrlObject | undefined | null = hrefOrig

    if (typeof href === 'string' && href.startsWith('https://mailto:')) {
      logger.error('ShwLink: href starts with "https://mailto:"', {
        href,
        currentPathname: pathname,
      })

      href = null
    }

    if (href == null) {
      // This is used when <RequiredLogin> removes the href of child <ShwLink> components.
      // We special case it to keep the logic in the main <ShwLinkWithHref> component simpler.
      return <ShwLinkWithoutHref {...props} ref={ref} />
    }
    return <ShwLinkWithHref href={href} {...props} ref={ref} />
  }
)
